/**
 * RiskExamMigrationPage
 * Unified page for migrating risks and exams across organizational levels
 * Combines the functionality of RiskApplicationPage and ExamApplicationPage
 *
 * @version 2025.1.0
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
  Badge,
  Chip,
  Stack,
  ListItemText,
} from '@mui/material';
import {
  Business as CompanyIcon,
  Domain as DepartmentIcon,
  Work as OccupationIcon,
  Person as EmployeeIcon,
  Warning as WarningIcon,
  Science as ExamIcon,
} from '@mui/icons-material';
import { PageShell } from '../../components';
import { DualTransferList, ExamApplicationModal, ExamExpirationBadge } from '../../components/exam-applications';
import { RiskApplicationModal, ExposureLevelBadge } from '../../components/risk-applications';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import { occupationService } from '../../services/occupationService';
import { employeeService } from '../../services/employeeService';
import {
  ApplicationLevel,
  ApplicationLevelLabels,
  ApplicationData,
  ExamWithApplication,
  AvailableExam,
  ExpirationStatus,
  getExpirationStatus,
  isExamWithApplication,
} from '../../types/examApplication';
import {
  RiskWithApplication,
  AvailableRisk,
  ExposureLevel,
  isRiskWithApplication,
} from '../../types/riskApplication';
import type { RiskApplicationScope } from '../../types/riskApplication';
import { useExamApplications } from '../exam-applications/hooks/useExamApplications';
import { useRiskApplications } from '../risk-applications/hooks/useRiskApplications';

// ============================================================================
// TYPES
// ============================================================================

interface SelectOption {
  id: number;
  name: string;
  parentId?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function TabPanel({ children, value, index }: TabPanelProps): React.ReactElement | null {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

const levelIcons: Record<ApplicationLevel, React.ReactElement> = {
  [ApplicationLevel.COMPANY]: <CompanyIcon />,
  [ApplicationLevel.DEPARTMENT]: <DepartmentIcon />,
  [ApplicationLevel.OCCUPATION]: <OccupationIcon />,
  [ApplicationLevel.EMPLOYEE]: <EmployeeIcon />,
};

// ============================================================================
// COMPONENT
// ============================================================================

export const RiskExamMigrationPage: React.FC = () => {
  // Tab state
  const [activeMainTab, setActiveMainTab] = useState<number>(0);
  const [activeSubTab, setActiveSubTab] = useState<number>(0);

  // Exam application state from hook
  const {
    applied: examApplied,
    available: examAvailable,
    alerts: examAlerts,
    loading: examLoading,
    loadingAlerts: examLoadingAlerts,
    error: examError,
    alertsError: examAlertsError,
    loadExams,
    loadAlerts: loadExamAlerts,
    applyMultipleExams,
    removeMultipleExams,
    updateApplication: updateExamApplication,
    clearError: clearExamError,
    clearAlertsError: clearExamAlertsError,
  } = useExamApplications();

  // Risk application state from hook
  const {
    applied: riskApplied,
    available: riskAvailable,
    alerts: riskAlerts,
    loading: riskLoading,
    loadingAlerts: riskLoadingAlerts,
    error: riskError,
    alertsError: riskAlertsError,
    loadRisks,
    loadAlerts: loadRiskAlerts,
    removeMultipleRisks,
    clearError: clearRiskError,
    clearAlertsError: clearRiskAlertsError,
  } = useRiskApplications();

  // Local UI state
  const [selectedLevel, setSelectedLevel] = useState<ApplicationLevel>(
    ApplicationLevel.COMPANY
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [selectedOccupationId, setSelectedOccupationId] = useState<number | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);

  // Options for selectors
  const [companies, setCompanies] = useState<SelectOption[]>([]);
  const [departments, setDepartments] = useState<SelectOption[]>([]);
  const [occupations, setOccupations] = useState<SelectOption[]>([]);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Exam Modal state
  const [examModalOpen, setExamModalOpen] = useState(false);
  const [examModalMode, setExamModalMode] = useState<'apply' | 'edit'>('apply');
  const [selectedExam, setSelectedExam] = useState<
    AvailableExam | ExamWithApplication | null
  >(null);
  const [pendingApplyExams, setPendingApplyExams] = useState<AvailableExam[]>([]);

  // Risk Modal state
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [riskModalMode, setRiskModalMode] = useState<'apply' | 'edit'>('apply');
  const [selectedRisk, setSelectedRisk] = useState<
    AvailableRisk | RiskWithApplication | null
  >(null);
  const [pendingApplyRisks, setPendingApplyRisks] = useState<AvailableRisk[]>([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ============================================================================
  // LOAD OPTIONS
  // ============================================================================

  // Load companies
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoadingOptions(true);
        const response = await companyService.getCompanies({ limit: 1000 });
        setCompanies(
          response.data.map((c) => ({
            id: Number(c.id),
            name: c.tradeName || c.legalName,
          }))
        );
      } catch (err) {
        console.error('Error loading companies:', err);
      } finally {
        setLoadingOptions(false);
      }
    };
    loadCompanies();
  }, []);

  // Load departments when company changes
  useEffect(() => {
    if (!selectedCompanyId) {
      setDepartments([]);
      return;
    }

    const loadDepartments = async () => {
      try {
        const response = await departmentService.getDepartments({
          companyId: String(selectedCompanyId),
          limit: 1000,
        });
        setDepartments(
          response.data.map((d) => ({
            id: Number(d.id),
            name: d.name,
            parentId: Number(d.companyId),
          }))
        );
      } catch (err) {
        console.error('Error loading departments:', err);
      }
    };
    loadDepartments();
  }, [selectedCompanyId]);

  // Load occupations when department changes
  useEffect(() => {
    if (!selectedDepartmentId) {
      setOccupations([]);
      return;
    }

    const loadOccupations = async () => {
      try {
        const response = await occupationService.getOccupations({
          limit: 1000,
          filters: { departmentId: selectedDepartmentId },
        });
        setOccupations(
          response.data.map((o) => ({
            id: o.id,
            name: o.name,
            parentId: o.departmentId,
          }))
        );
      } catch (err) {
        console.error('Error loading occupations:', err);
      }
    };
    loadOccupations();
  }, [selectedDepartmentId]);

  // Load employees when occupation changes
  useEffect(() => {
    if (!selectedOccupationId) {
      setEmployees([]);
      return;
    }

    const loadEmployees = async () => {
      try {
        const response = await employeeService.getEmployees({
          limit: 1000,
          filters: { occupationId: selectedOccupationId },
        });
        setEmployees(
          response.data.map((e) => ({
            id: e.id,
            name: e.name,
            parentId: e.occupationId,
          }))
        );
      } catch (err) {
        console.error('Error loading employees:', err);
      }
    };
    loadEmployees();
  }, [selectedOccupationId]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const currentTargetId = useMemo((): number | null => {
    switch (selectedLevel) {
      case ApplicationLevel.COMPANY:
        return selectedCompanyId;
      case ApplicationLevel.DEPARTMENT:
        return selectedDepartmentId;
      case ApplicationLevel.OCCUPATION:
        return selectedOccupationId;
      case ApplicationLevel.EMPLOYEE:
        return selectedEmployeeId;
    }
  }, [
    selectedLevel,
    selectedCompanyId,
    selectedDepartmentId,
    selectedOccupationId,
    selectedEmployeeId,
  ]);

  const currentTargetName = useMemo((): string => {
    switch (selectedLevel) {
      case ApplicationLevel.COMPANY:
        return companies.find((c) => c.id === selectedCompanyId)?.name || '';
      case ApplicationLevel.DEPARTMENT:
        return departments.find((d) => d.id === selectedDepartmentId)?.name || '';
      case ApplicationLevel.OCCUPATION:
        return occupations.find((o) => o.id === selectedOccupationId)?.name || '';
      case ApplicationLevel.EMPLOYEE:
        return employees.find((e) => e.id === selectedEmployeeId)?.name || '';
    }
  }, [
    selectedLevel,
    selectedCompanyId,
    selectedDepartmentId,
    selectedOccupationId,
    selectedEmployeeId,
    companies,
    departments,
    occupations,
    employees,
  ]);

  const examAlertsCounts = useMemo(() => {
    if (!examAlerts) return { expiring: 0, expired: 0 };
    return {
      expiring: examAlerts.expiring.length,
      expired: examAlerts.expired.length,
    };
  }, [examAlerts]);

  const riskAlertsCounts = useMemo(() => {
    if (!riskAlerts) return { critical: 0, veryHigh: 0, high: 0 };
    return {
      critical: riskAlerts.summary?.totalCritical || 0,
      veryHigh: riskAlerts.summary?.veryHighCount || 0,
      high: riskAlerts.summary?.highCount || 0,
    };
  }, [riskAlerts]);

  const currentError = activeMainTab === 0 ? riskError : examError;

  // ============================================================================
  // LOAD DATA WHEN TARGET CHANGES
  // ============================================================================

  useEffect(() => {
    if (currentTargetId) {
      // Load both risks and exams when target changes
      loadRisks(selectedLevel, currentTargetId);
      loadExams(selectedLevel, currentTargetId);
    }
  }, [selectedLevel, currentTargetId, loadRisks, loadExams]);

  // Load alerts when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      loadRiskAlerts(selectedCompanyId);
      loadExamAlerts(selectedCompanyId, 30);
    }
  }, [selectedCompanyId, loadRiskAlerts, loadExamAlerts]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleLevelChange = (level: ApplicationLevel) => {
    setSelectedLevel(level);
    // Reset dependent selections
    if (level === ApplicationLevel.COMPANY) {
      setSelectedDepartmentId(null);
      setSelectedOccupationId(null);
      setSelectedEmployeeId(null);
    } else if (level === ApplicationLevel.DEPARTMENT) {
      setSelectedOccupationId(null);
      setSelectedEmployeeId(null);
    } else if (level === ApplicationLevel.OCCUPATION) {
      setSelectedEmployeeId(null);
    }
  };

  // Exam Handlers
  const handleExamApply = useCallback(
    async (items: (AvailableExam | ExamWithApplication)[]) => {
      const availableItems = items.filter(
        (item): item is AvailableExam => !isExamWithApplication(item)
      );

      if (availableItems.length === 0) return;

      setPendingApplyExams(availableItems);
      setSelectedExam(availableItems[0]);
      setExamModalMode('apply');
      setExamModalOpen(true);
    },
    []
  );

  const handleExamRemove = useCallback(
    async (items: (AvailableExam | ExamWithApplication)[]) => {
      if (!currentTargetId) return;

      const appliedItems = items.filter(
        (item): item is ExamWithApplication => isExamWithApplication(item)
      );

      if (appliedItems.length === 0) return;

      const examIds = appliedItems.map((item) => item.examId);
      const success = await removeMultipleExams(selectedLevel, currentTargetId, examIds);

      if (success) {
        setSnackbar({
          open: true,
          message: `${examIds.length} exame(s) removido(s) com sucesso`,
          severity: 'success',
        });
      }
    },
    [currentTargetId, selectedLevel, removeMultipleExams]
  );

  const handleExamModalConfirm = useCallback(
    async (data: ApplicationData) => {
      if (!currentTargetId || pendingApplyExams.length === 0) return;

      const examIds = pendingApplyExams.map((exam) => exam.id);
      const success = await applyMultipleExams(
        selectedLevel,
        currentTargetId,
        examIds,
        data
      );

      if (success) {
        setSnackbar({
          open: true,
          message: `${examIds.length} exame(s) aplicado(s) com sucesso`,
          severity: 'success',
        });
        setExamModalOpen(false);
        setPendingApplyExams([]);
        setSelectedExam(null);
      }
    },
    [currentTargetId, selectedLevel, pendingApplyExams, applyMultipleExams]
  );

  const handleExamEditConfirm = useCallback(
    async (data: ApplicationData) => {
      if (!currentTargetId || !selectedExam || !isExamWithApplication(selectedExam)) return;

      const success = await updateExamApplication(
        selectedLevel,
        currentTargetId,
        selectedExam.examId,
        data
      );

      if (success) {
        setSnackbar({
          open: true,
          message: 'Aplicacao de exame atualizada com sucesso',
          severity: 'success',
        });
        setExamModalOpen(false);
        setSelectedExam(null);
      }
    },
    [currentTargetId, selectedLevel, selectedExam, updateExamApplication]
  );

  const handleExamItemEdit = useCallback(
    (item: AvailableExam | ExamWithApplication) => {
      if (!isExamWithApplication(item)) return;

      setSelectedExam(item);
      setExamModalMode('edit');
      setExamModalOpen(true);
    },
    []
  );

  const handleCloseExamModal = useCallback(() => {
    setExamModalOpen(false);
    setPendingApplyExams([]);
    setSelectedExam(null);
  }, []);

  // Risk Handlers
  const handleRiskApply = useCallback(
    async (items: (AvailableRisk | RiskWithApplication)[]) => {
      const availableItems = items.filter(
        (item): item is AvailableRisk => !isRiskWithApplication(item)
      );

      if (availableItems.length === 0) return;

      setPendingApplyRisks(availableItems);
      setSelectedRisk(availableItems[0]);
      setRiskModalMode('apply');
      setRiskModalOpen(true);
    },
    []
  );

  const handleRiskRemove = useCallback(
    async (items: (AvailableRisk | RiskWithApplication)[]) => {
      if (!currentTargetId) return;

      const appliedItems = items.filter(
        (item): item is RiskWithApplication => isRiskWithApplication(item)
      );

      if (appliedItems.length === 0) return;

      const riskIds = appliedItems.map((item) => item.riskId);
      const success = await removeMultipleRisks(selectedLevel, currentTargetId, riskIds);

      if (success) {
        setSnackbar({
          open: true,
          message: `${riskIds.length} risco(s) removido(s) com sucesso`,
          severity: 'success',
        });
      }
    },
    [currentTargetId, selectedLevel, removeMultipleRisks]
  );

  const levelToScope: Record<ApplicationLevel, RiskApplicationScope> = useMemo(() => ({
    [ApplicationLevel.COMPANY]: 'company',
    [ApplicationLevel.DEPARTMENT]: 'department',
    [ApplicationLevel.OCCUPATION]: 'occupation',
    [ApplicationLevel.EMPLOYEE]: 'employee',
  }), []);

  const handleRiskSaved = useCallback(() => {
    if (!currentTargetId) return;
    loadRisks(selectedLevel, currentTargetId);
    setSnackbar({
      open: true,
      message: riskModalMode === 'apply' ? 'Risco aplicado com sucesso' : 'Aplicacao de risco atualizada com sucesso',
      severity: 'success',
    });
    setRiskModalOpen(false);
    setPendingApplyRisks([]);
    setSelectedRisk(null);
  }, [currentTargetId, selectedLevel, loadRisks, riskModalMode]);

  const handleRiskItemEdit = useCallback(
    (item: AvailableRisk | RiskWithApplication) => {
      if (!isRiskWithApplication(item)) return;

      setSelectedRisk(item);
      setRiskModalMode('edit');
      setRiskModalOpen(true);
    },
    []
  );

  const handleCloseRiskModal = useCallback(() => {
    setRiskModalOpen(false);
    setPendingApplyRisks([]);
    setSelectedRisk(null);
  }, []);

  const handleClearError = useCallback(() => {
    if (activeMainTab === 0) {
      clearRiskError();
    } else {
      clearExamError();
    }
  }, [activeMainTab, clearRiskError, clearExamError]);

  // ============================================================================
  // RENDER
  // ============================================================================

  const renderLevelSelector = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Selecionar Nível e Destino" />
      <CardContent>
        <Grid container spacing={3}>
          {/* Level Selector */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth>
              <InputLabel>Nível</InputLabel>
              <Select
                value={selectedLevel}
                label="Nível"
                onChange={(e) => handleLevelChange(e.target.value as ApplicationLevel)}
              >
                {Object.values(ApplicationLevel).map((level) => (
                  <MenuItem key={level} value={level}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {levelIcons[level]}
                      <span>{ApplicationLevelLabels[level]}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Company Selector (always shown) */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={selectedCompanyId || ''}
                label="Empresa"
                onChange={(e) => {
                  setSelectedCompanyId(Number(e.target.value));
                  setSelectedDepartmentId(null);
                  setSelectedOccupationId(null);
                  setSelectedEmployeeId(null);
                }}
                disabled={loadingOptions}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Department Selector (shown for department, occupation, employee) */}
          {selectedLevel !== ApplicationLevel.COMPANY && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth>
                <InputLabel>Setor</InputLabel>
                <Select
                  value={selectedDepartmentId || ''}
                  label="Setor"
                  onChange={(e) => {
                    setSelectedDepartmentId(Number(e.target.value));
                    setSelectedOccupationId(null);
                    setSelectedEmployeeId(null);
                  }}
                  disabled={!selectedCompanyId || departments.length === 0}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Occupation Selector (shown for occupation, employee) */}
          {(selectedLevel === ApplicationLevel.OCCUPATION ||
            selectedLevel === ApplicationLevel.EMPLOYEE) && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth>
                <InputLabel>Cargo</InputLabel>
                <Select
                  value={selectedOccupationId || ''}
                  label="Cargo"
                  onChange={(e) => {
                    setSelectedOccupationId(Number(e.target.value));
                    setSelectedEmployeeId(null);
                  }}
                  disabled={!selectedDepartmentId || occupations.length === 0}
                >
                  {occupations.map((occ) => (
                    <MenuItem key={occ.id} value={occ.id}>
                      {occ.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Employee Selector (shown for employee only) */}
          {selectedLevel === ApplicationLevel.EMPLOYEE && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth>
                <InputLabel>Funcionário</InputLabel>
                <Select
                  value={selectedEmployeeId || ''}
                  label="Funcionário"
                  onChange={(e) => setSelectedEmployeeId(Number(e.target.value))}
                  disabled={!selectedOccupationId || employees.length === 0}
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        {/* Current Target Info */}
        {currentTargetId && currentTargetName && (
          <Box sx={{ mt: 2 }}>
            <Chip
              icon={levelIcons[selectedLevel]}
              label={`${ApplicationLevelLabels[selectedLevel]}: ${currentTargetName}`}
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderRiskTransferList = () => (
    <Card>
      <CardHeader
        title="Gerenciar Riscos"
        subheader={
          currentTargetId
            ? `Aplicar ou remover riscos de ${ApplicationLevelLabels[selectedLevel].toLowerCase()}`
            : 'Selecione um destino para gerenciar riscos'
        }
      />
      <CardContent>
        {!currentTargetId ? (
          <Alert severity="info">
            Selecione uma empresa e o nivel de aplicacao para gerenciar os riscos.
          </Alert>
        ) : riskLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DualTransferList
            available={riskAvailable}
            applied={riskApplied}
            onApply={handleRiskApply}
            onRemove={handleRiskRemove}
            getItemKey={(item) => (isRiskWithApplication(item) ? item.riskId : item.id)}
            getItemLabel={(item) => (isRiskWithApplication(item) ? item.riskName : item.risk)}
            getItemSecondary={(item) => item.riskFactorName}
            searchFields={['riskFactorName', 'hazard']}
            availableTitle="Riscos Disponiveis"
            appliedTitle="Riscos Aplicados"
            loading={riskLoading}
            renderItem={(item, isApplied) => (
              <ListItemText
                primary={isRiskWithApplication(item) ? item.riskName : item.risk}
                secondary={
                  isApplied && isRiskWithApplication(item) ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <ExposureLevelBadge
                        level={item.exposureLevel}
                        size="small"
                        exposureDays={item.exposureDays}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {item.riskFactorName}
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={item.riskFactorName} size="small" variant="outlined" />
                      <Typography variant="caption" color="text.secondary">
                        {item.hazard}
                      </Typography>
                    </Stack>
                  )
                }
              />
            )}
            onItemEdit={handleRiskItemEdit}
          />
        )}
      </CardContent>
    </Card>
  );

  const renderExamTransferList = () => (
    <Card>
      <CardHeader
        title="Gerenciar Exames"
        subheader={
          currentTargetId
            ? `Aplicar ou remover exames de ${ApplicationLevelLabels[selectedLevel].toLowerCase()}`
            : 'Selecione um destino para gerenciar exames'
        }
      />
      <CardContent>
        {!currentTargetId ? (
          <Alert severity="info">
            Selecione uma empresa e o nivel de aplicacao para gerenciar os exames.
          </Alert>
        ) : examLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DualTransferList
            available={examAvailable}
            applied={examApplied}
            onApply={handleExamApply}
            onRemove={handleExamRemove}
            getItemKey={(item) => (isExamWithApplication(item) ? item.examId : item.id)}
            getItemLabel={(item) => item.examName}
            getItemSecondary={(item) =>
              'esocialCode' in item && item.esocialCode ? item.esocialCode : undefined
            }
            searchFields={['examName', 'esocialCode']}
            availableTitle="Exames Disponiveis"
            appliedTitle="Exames Aplicados"
            loading={examLoading}
            renderItem={(item, isApplied) => (
              <ListItemText
                primary={item.examName}
                secondary={
                  isApplied && isExamWithApplication(item) ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <ExamExpirationBadge
                        status={getExpirationStatus(item)}
                        daysUntilExpiration={item.daysUntilExpiration}
                        size="small"
                      />
                      {item.periodicityMonths && (
                        <Typography variant="caption" color="text.secondary">
                          {item.periodicityMonths} meses
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    item.esocialCode ?? undefined
                  )
                }
              />
            )}
            onItemEdit={handleExamItemEdit}
          />
        )}
      </CardContent>
    </Card>
  );

  const renderRiskAlertsTab = () => (
    <Card>
      <CardHeader
        title="Alertas de Exposicao Critica"
        subheader="Riscos com nivel de exposicao alto ou muito alto"
      />
      <CardContent>
        {!selectedCompanyId ? (
          <Alert severity="info">
            Selecione uma empresa para visualizar os alertas de exposicao critica.
          </Alert>
        ) : riskLoadingAlerts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : riskAlertsError ? (
          <Alert severity="error" onClose={clearRiskAlertsError}>
            {riskAlertsError}
          </Alert>
        ) : !riskAlerts || riskAlerts.critical.length === 0 ? (
          <Alert severity="success">
            Nenhum risco com nivel de exposicao critico encontrado.
          </Alert>
        ) : (
          <Box>
            {/* Summary */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <Chip
                label={`Total: ${riskAlertsCounts.critical}`}
                color="default"
              />
              <Chip
                label={`Muito Alto: ${riskAlertsCounts.veryHigh}`}
                color="error"
              />
              <Chip
                label={`Alto: ${riskAlertsCounts.high}`}
                color="warning"
              />
            </Box>

            {/* Critical Alerts List */}
            {riskAlerts.critical.map((alert) => (
              <Card
                key={`critical-${alert.applicationId}`}
                variant="outlined"
                sx={{
                  mb: 1,
                  borderColor:
                    alert.exposureLevel === ExposureLevel.VERY_HIGH
                      ? 'error.main'
                      : 'warning.main',
                }}
              >
                <CardContent sx={{ py: 1.5 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="subtitle2">{alert.riskName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ApplicationLevelLabels[alert.targetType]}: {alert.targetName}
                      </Typography>
                    </Box>
                    <ExposureLevelBadge
                      level={alert.exposureLevel}
                      exposureDays={alert.exposureDays}
                    />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderExamAlertsTab = () => (
    <Card>
      <CardHeader
        title="Alertas de Vencimento"
        subheader="Exames proximos do vencimento ou ja vencidos"
      />
      <CardContent>
        {!selectedCompanyId ? (
          <Alert severity="info">
            Selecione uma empresa para visualizar os alertas de vencimento.
          </Alert>
        ) : examLoadingAlerts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : examAlertsError ? (
          <Alert severity="error" onClose={clearExamAlertsError}>
            {examAlertsError}
          </Alert>
        ) : !examAlerts || (examAlerts.expired.length === 0 && examAlerts.expiring.length === 0) ? (
          <Alert severity="success">
            Nenhum exame vencido ou próximo do vencimento.
          </Alert>
        ) : (
          <Box>
            {examAlerts.expired.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                  Exames Vencidos ({examAlerts.expired.length})
                </Typography>
                {examAlerts.expired.map((alert) => (
                  <Card
                    key={`expired-${alert.applicationId}`}
                    variant="outlined"
                    sx={{ mb: 1, borderColor: 'error.main' }}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="subtitle2">{alert.examName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ApplicationLevelLabels[alert.targetType]}: {alert.targetName}
                          </Typography>
                        </Box>
                        <ExamExpirationBadge
                          status={ExpirationStatus.EXPIRED}
                          daysUntilExpiration={-alert.daysOverdue}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {examAlerts.expiring.length > 0 && (
              <Box>
                <Typography variant="h6" color="warning.main" sx={{ mb: 2 }}>
                  Exames Vencendo ({examAlerts.expiring.length})
                </Typography>
                {examAlerts.expiring.map((alert) => (
                  <Card
                    key={`expiring-${alert.applicationId}`}
                    variant="outlined"
                    sx={{ mb: 1, borderColor: 'warning.main' }}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="subtitle2">{alert.examName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ApplicationLevelLabels[alert.targetType]}: {alert.targetName}
                          </Typography>
                        </Box>
                        <ExamExpirationBadge
                          status={ExpirationStatus.EXPIRING_SOON}
                          daysUntilExpiration={alert.daysUntilExpiration}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Seguranca', path: '/seguranca' },
        { label: 'Migracao de Riscos e Exames' },
      ]}
      title="Migracao de Riscos e Exames"
      subtitle="Gerencie a aplicacao de riscos e exames em empresas, setores, cargos e funcionarios de forma unificada"
    >
      {/* Error Alert */}
      {currentError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={handleClearError}>
          {currentError}
        </Alert>
      )}

      {/* Level Selector */}
      {renderLevelSelector()}

      {/* Main Tabs (Risks / Exams) */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeMainTab} onChange={(_, newValue) => setActiveMainTab(newValue)}>
          <Tab
            label={
              <Badge
                badgeContent={riskAlertsCounts.critical}
                color="error"
                max={99}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon fontSize="small" />
                  Riscos
                </Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge
                badgeContent={examAlertsCounts.expired + examAlertsCounts.expiring}
                color="error"
                max={99}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ExamIcon fontSize="small" />
                  Exames
                </Box>
              </Badge>
            }
          />
        </Tabs>
      </Box>

      {/* Risks Tab Panel */}
      <TabPanel value={activeMainTab} index={0}>
        {/* Sub-tabs for Risks */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeSubTab} onChange={(_, newValue) => setActiveSubTab(newValue)}>
            <Tab label="Gerenciar Riscos" />
            <Tab
              label={
                <Badge
                  badgeContent={riskAlertsCounts.critical}
                  color="error"
                  max={99}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon fontSize="small" />
                    Alertas Criticos
                  </Box>
                </Badge>
              }
            />
          </Tabs>
        </Box>

        <TabPanel value={activeSubTab} index={0}>
          {renderRiskTransferList()}
        </TabPanel>

        <TabPanel value={activeSubTab} index={1}>
          {renderRiskAlertsTab()}
        </TabPanel>
      </TabPanel>

      {/* Exams Tab Panel */}
      <TabPanel value={activeMainTab} index={1}>
        {/* Sub-tabs for Exams */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeSubTab} onChange={(_, newValue) => setActiveSubTab(newValue)}>
            <Tab label="Gerenciar Exames" />
            <Tab
              label={
                <Badge
                  badgeContent={examAlertsCounts.expired + examAlertsCounts.expiring}
                  color="error"
                  max={99}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon fontSize="small" />
                    Alertas
                  </Box>
                </Badge>
              }
            />
          </Tabs>
        </Box>

        <TabPanel value={activeSubTab} index={0}>
          {renderExamTransferList()}
        </TabPanel>

        <TabPanel value={activeSubTab} index={1}>
          {renderExamAlertsTab()}
        </TabPanel>
      </TabPanel>

      {/* Exam Modal */}
      <ExamApplicationModal
        open={examModalOpen}
        onClose={handleCloseExamModal}
        onConfirm={examModalMode === 'apply' ? handleExamModalConfirm : handleExamEditConfirm}
        exam={selectedExam}
        mode={examModalMode}
        loading={examLoading}
        error={examError}
      />

      {/* Risk Modal */}
      {riskModalOpen && currentTargetId != null && (
        riskModalMode === 'apply' && pendingApplyRisks.length > 0 ? (
          <RiskApplicationModal
            open={riskModalOpen}
            onClose={handleCloseRiskModal}
            onSaved={handleRiskSaved}
            scope={levelToScope[selectedLevel]}
            scopeId={currentTargetId}
            mode="apply"
            riskId={pendingApplyRisks[0].id}
            riskName={pendingApplyRisks[0].risk}
            riskFactorName={pendingApplyRisks[0].riskFactorName}
          />
        ) : riskModalMode === 'edit' && selectedRisk != null && isRiskWithApplication(selectedRisk) ? (
          <RiskApplicationModal
            open={riskModalOpen}
            onClose={handleCloseRiskModal}
            onSaved={handleRiskSaved}
            scope={levelToScope[selectedLevel]}
            scopeId={currentTargetId}
            mode="edit"
            applicationId={selectedRisk.id}
            riskName={selectedRisk.riskName}
            riskFactorName={selectedRisk.riskFactorName}
          />
        ) : null
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default RiskExamMigrationPage;
